import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ContentGrid } from '../../../partials/ContentGrid'
import SummaryPicture from '../../../partials/ContentGrid/SummaryPicture'
import Accordion from '../../../partials/Accordion/Accordion'
import { AccordionItemData } from '../../../partials/Accordion/components/AccordionItem'
import SectionWithTag from '../../../components/SectionWithTag'
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import FadeIn from '../../../components/Animation/FadeIn'
import { useIsMobile } from '../../../utils/helpers'
import { SetPaddingProps } from '../../../utils/PaddingContext'

const id = 'page.applications.howItWorks'
const data: AccordionItemData[] = [
    {
        id: `${id}.manufacturing`,
        slug: 'manufacturing',
    },
    {
        id: `${id}.molecular-biology`,
        slug: 'molecular-biology',
    },
    {
        id: `${id}.food`,
        slug: 'food',
    },
    {
        id: `${id}.biotherapeutics`,
        slug: 'biotherapeutics',
    },
    {
        id: `${id}.agriculture`,
        slug: 'agriculture',
    },
]

const timeout = 7000

const HowItWorks = ({ setPadding }: SetPaddingProps) => {
    const [activeItem, setActiveItem] = useState(0)
    const isMobile = useIsMobile()

    useEffect(() => {
        let intervalID: NodeJS.Timeout
        if (!isMobile) {
            intervalID = setInterval(
                () => setActiveItem(activeItem < data.length - 1 ? activeItem + 1 : 0),
                timeout,
            )
        }

        return () => {
            clearInterval(intervalID)
        }
    }, [activeItem, isMobile])

    const leftRef = useRef<HTMLDivElement>(null)
    const rightRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        const calculatePadding = () => {
            requestAnimationFrame(() => {
                if (leftRef.current && rightRef.current && contentRef.current && !isMobile) {
                    const paddingLeft =
            leftRef.current.getBoundingClientRect().left -
            contentRef.current.getBoundingClientRect().left
                    const paddingRight =
            window.innerWidth -
            rightRef.current.getBoundingClientRect().right -
            contentRef.current.getBoundingClientRect().left
                    setPadding({
                        paddingLeft,
                        paddingRight: paddingRight === 0 ? paddingLeft : paddingRight,
                    })
                } else {
                    setPadding()
                }
            })
        }
        calculatePadding()
        window.addEventListener('resize', calculatePadding)
        return () => {
            window.removeEventListener('resize', calculatePadding)
        }
    }, [leftRef, rightRef, isMobile, contentRef, loaded, setPadding])
    return (
        <div className='relative'>
            <div className='py-12 md:pt-30 md:pb-20 border-b border-black/10 '>
                <FadeIn>
                    <SectionWithTag tag={{ id: `${id}.tag` }} className='3xl:max-w-screen-2xl mx-auto'>
                        <div className='text-center pt-9 md:pt-0 max-w-[350px] md:max-w-[700px] mx-auto'>
                            <FormattedMessage id={id} tagName='h2' />
                        </div>
                    </SectionWithTag>
                </FadeIn>
            </div>
            <ContentGrid className='3xl:max-w-screen-2xl mx-auto'>
                <div className='side-padding py-10 xl:py-24 bg-white aspect-square' ref={contentRef}>
                    <div ref={leftRef} className='flex items-center md:h-fit w-full max-w-[410px] mx-auto'>
                        <Accordion
                            data={data}
                            setActiveItem={setActiveItem}
                            activeItem={activeItem}
                            timeout={timeout}
                        />
                    </div>
                </div>
                <div className='hidden md:block flex items-center '>
                    <div className=' h-full p-[15%]' ref={rightRef}>
                        <SwitchTransition mode='out-in'>
                            <CSSTransition
                                key={activeItem}
                                addEndListener={(node, done) => {
                                    node.addEventListener('transitionend', done, false)
                                }}
                                classNames='fade'
                            >
                                <SummaryPicture
                                    slug={data[activeItem].slug || data[activeItem].id}
                                    imageClassName='w-full'
                                    className='aspect-square'
                                    onLoad={() => setLoaded(true)}
                                />
                            </CSSTransition>
                        </SwitchTransition>
                    </div>
                </div>
            </ContentGrid>
        </div>
    )
}

export default HowItWorks
